import React from 'react';

import useWidgetRouterStore from './widgetRouterStore';
import ProcedureWidget from '../impl/ProcedureWidget';
import DiagnosisWidget from '../impl/DiagnosisWidget';
import FollowUpWidget from '../impl/FollowUpWidget';
import VisitCancelWidget from '../impl/VisitCancelWidget';
import TaskResolutionWidget from '../impl/TaskResolutionWidget';

/**
 * WidgetHost — mounted per-consultation in `DoctorQueueView` (active-
 * visit branch). Renders the persistent visit-scoped rail (the
 * procedure half-disc) and ALSO routes any active spawn-on-demand
 * widget for the consultation context — so chart→widget flows where
 * the chart is open keep their direct, single-component path.
 *
 * For pages that don't mount WidgetHost (e.g. ManageVisitPage) the
 * active widget is rendered by `<ActiveWidgetOverlay />` (also mounted
 * at app root). Both observe the same `widgetRouterStore`; at most one
 * top-level page is mounted at a time, so the active widget renders
 * exactly once.
 *
 * MVP constraint: the widget switch is hardcoded here. Plugin #2 will
 * lift this into a `widgetRegistry` lookup — mechanical refactor, this
 * file is the only thing that changes.
 */
export default function WidgetHost({
  visit,
  viewer,
  onProcedureWidthChange,
  onProcedureCoarseShrunkTap,   // optional: chart-aware coarse-pointer handoff (see ProcedureWidget)
  procedurePairedFullSize,      // optional: integrator-provided signal that a sibling pane is mounted alongside the procedure rail; forwarded as ProcedureWidget's `pairedFullSize` prop
}) {
  const activeWidget = useWidgetRouterStore((s) => s.active);
  const closeWidget = useWidgetRouterStore((s) => s.closeWidget);

  return (
    <>
      <ProcedureWidget
        visit={visit}
        viewer={viewer}
        onWidthChange={onProcedureWidthChange}
        onCoarseShrunkTap={onProcedureCoarseShrunkTap}
        pairedFullSize={procedurePairedFullSize}
      />

      {activeWidget && (
        <ActiveWidgetSlot
          kind={activeWidget.kind}
          context={activeWidget.context}
          opts={activeWidget.opts}
          visit={visit}
          viewer={viewer}
          onClose={closeWidget}
        />
      )}
    </>
  );
}

export function ActiveWidgetSlot({ kind, context, opts, visit, viewer, onClose }) {
  switch (kind) {
    case 'procedure':
      // Procedure widget owns its own consumption via
      // `useProcedurePrefillBridge` (subscribes to the store directly).
      // When the chart fires `openWidget('procedure', ...)`, the bridge
      // expands the rail, captures the prefill, and calls
      // `closeWidget()` from inside ProcedureWidget. The Host returns
      // null here — no setTimeout-close band-aid needed any more.
      //
      // Chart-pane placement: the chart itself hosts the inline picker
      // via its own SDK subscriber + portal. The bridge skips
      // expansion in that branch; the chart closes the store when its
      // inline picker saves or cancels.
      return null;

    case 'diagnosis':
      return (
        <DiagnosisWidget
          context={context}
          opts={opts}
          visit={visit}
          viewer={viewer}
          onClose={onClose}
        />
      );

    case 'followUp':
      return (
        <FollowUpWidget
          context={context}
          opts={opts}
          onClose={onClose}
        />
      );

    case 'visitCancel':
      return (
        <VisitCancelWidget
          context={context}
          opts={opts}
          onClose={onClose}
        />
      );

    case 'taskResolution':
      return (
        <TaskResolutionWidget
          context={context}
          opts={opts}
          onClose={onClose}
        />
      );

    default:
      return null;
  }
}
